import React, { useState, useRef, useCallback, useEffect } from 'react';

interface LongPressButtonProps {
    onComplete: () => void;
    duration?: number;
    className?: string;
    children: React.ReactNode;
    disabled?: boolean;
}

export const LongPressButton: React.FC<LongPressButtonProps> = ({
    onComplete,
    duration = 1500,
    className = '',
    children,
    disabled = false,
}) => {
    const [pressing, setPressing] = useState(false);
    const [progress, setProgress] = useState(0);
    const timerRef = useRef<any>(null);
    const startRef = useRef<number>(0);

    const startPress = useCallback(() => {
        if (disabled) return;
        setPressing(true);
        setProgress(0);
        startRef.current = Date.now();

        timerRef.current = setInterval(() => {
            const elapsed = Date.now() - startRef.current;
            const p = Math.min((elapsed / duration) * 100, 100);
            setProgress(p);

            if (p >= 100) {
                clearInterval(timerRef.current);
                setPressing(false);
                onComplete();
            }
        }, 20);
    }, [disabled, duration, onComplete]);

    const endPress = useCallback(() => {
        setPressing(false);
        setProgress(0);
        if (timerRef.current) clearInterval(timerRef.current);
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    return (
        <button
            onMouseDown={startPress}
            onMouseUp={endPress}
            onMouseLeave={endPress}
            onTouchStart={startPress}
            onTouchEnd={endPress}
            disabled={disabled}
            className={`relative overflow-hidden transition-all active:scale-[0.98] ${className} ${pressing ? 'brightness-110' : ''}`}
        >
            <div className="relative z-10 flex items-center justify-center gap-2">
                {children}
            </div>

            {/* Progress Overlay */}
            {pressing && (
                <div
                    className="absolute left-0 bottom-0 top-0 bg-white/20 transition-all duration-75 ease-linear pointer-events-none"
                    style={{ width: `${progress}%` }}
                />
            )}
        </button>
    );
};
